<template>
  <div class="demographic-profile">
    <div class="demographic-profile__cards">
      <KpiCard
        v-for="card in kpis"
        :key="card.title"
        :title="card.title"
        :value="card.value"
        :complement="card.complement"
        :complement-variant="card.complementVariant"
        :description="card.description"
      />
    </div>

    <div class="demographic-profile__grid demographic-profile__grid--top">
      <AgeRangePyramidChart
        :items="ageGenderItems"
        :exam-options="availableExams"
        :exam="selectedExam"
        :loading="loading.overview || loading.ageGender"
        @exam-change="onExamChange"
      />

      <div class="demographic-profile__panel">
        <div class="demographic-profile__panel-title">Top 5 Exames por Perfil</div>
        <BiChartEmpty v-if="!loading.overview && !loading.profile && !hasProfileData" />
        <template v-else>
          <div class="demographic-profile__filters demographic-profile__filters--profile">
            <select
              v-model="selectedGender"
              class="demographic-profile__select"
            >
              <option
                v-for="option in genderOptions"
                :key="option"
                :value="option"
              >
                Gênero: {{ genderLabels[option] }}
              </option>
            </select>
            <select
              v-model="selectedAgeRange"
              class="demographic-profile__select"
            >
              <option
                v-for="option in ageRangeOptions"
                :key="option"
                :value="option"
              >
                Faixa Etária: {{ ageRangeLabels[option] }}
              </option>
            </select>
          </div>
          <div
            v-if="loading.overview || loading.profile"
            class="demographic-profile__list-skeleton"
          >
            <span
              v-for="row in 5"
              :key="row"
            />
          </div>
          <Top5List
            v-else
            :show-title="false"
            :last-row-margin="0"
            :data="getProfileExams()"
            :columns="profileColumns"
            sort-key="sortValue"
          />
        </template>
      </div>
    </div>

    <div class="demographic-profile__panel">
      <div class="demographic-profile__panel-title">
        Evolução das Top 5 Especialidades
      </div>
      <BiChartEmpty v-if="!loading.overview && !loading.seasonal && !hasSpecialtyData" />
      <template v-else>
        <div class="demographic-profile__filters">
          <button
            v-for="(filter, index) in specialtyFilters"
            :key="filter.code"
            type="button"
            :class="{
              'demographic-profile__filter--active': index === selectedSpecialtyIndex,
            }"
            @click="selectedSpecialtyIndex = index"
          >
            {{ filter.description }}
          </button>
        </div>
        <div
          class="demographic-profile__grid demographic-profile__grid--detail demographic-profile__grid--specialties"
        >
          <LineChart
            :categories="months"
            :values="getSpecialtyEvolution()"
            :loading="loading.overview || loading.seasonal"
            series-name="Exames"
            :tooltip-subtitle="selectedSpecialtyDescription"
            unit=" exames"
            :decimals="0"
            :value-formatter="formatExamCount"
            :y-axis-formatter="formatExamCount"
            :show-target="false"
            :show-background="false"
          />
          <div
            v-if="loading.overview || loading.seasonal"
            class="demographic-profile__list-skeleton"
          >
            <span
              v-for="row in 5"
              :key="row"
            />
          </div>
          <Top5List
            v-else
            :show-title="false"
            :data="getSpecialtyExams()"
            :columns="specialtyColumns"
            sort-key="sortValue"
          />
        </div>
        <button
          type="button"
          class="demographic-profile__view-all"
          @click="onViewAll"
        >
          Ver todos
          <md-icon md-src="/static/images/icon-download-arrow.svg" />
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import KpiCard from '../charts/KpiCard.vue'
import AgeRangePyramidChart from '../charts/AgeRangePyramidChart.vue'
import LineChart from '../charts/LineChart.vue'
import Top5List from '../charts/Top5List.vue'
import biDemographicProfile from '../../../api/biDemographicProfile'
import BiChartEmpty from '../charts/BiChartEmpty.vue'
import { hasChartData } from '../charts/chartData'

const GENDER_LABELS = { ALL: 'Todos', M: 'Masculino', F: 'Feminino' }
const AGE_RANGE_LABELS = {
  ALL: 'Todas',
  '0-17': '0-17',
  '18-29': '18-29',
  '30-44': '30-44',
  '45-59': '45-59',
  '60+': '60+',
}

export default {
  name: 'DemographicProfile',
  components: {
    KpiCard,
    AgeRangePyramidChart,
    LineChart,
    Top5List,
    BiChartEmpty,
  },
  props: {
    period: { type: String, default: null },
  },
  data() {
    return {
      loading: {
        overview: false,
        profile: false,
        ageGender: false,
        seasonal: false,
      },
      kpis: [
        { title: 'OS cadastradas', value: '--' },
        { title: 'Exames realizados', value: '--' },
        { title: 'Média de Exames por OS', value: '--' },
      ],
      availableExams: [],
      selectedExam: 'ALL',
      genderOptions: Object.keys(GENDER_LABELS),
      ageRangeOptions: Object.keys(AGE_RANGE_LABELS),
      genderLabels: GENDER_LABELS,
      ageRangeLabels: AGE_RANGE_LABELS,
      selectedGender: 'ALL',
      selectedAgeRange: 'ALL',
      ageGenderItems: [],
      topExamsByProfile: [],
      months: [],
      specialtyFilters: [],
      selectedSpecialtyIndex: 0,
      specialtyEvolution: [],
      topSpecialtyExams: [],
      profileColumns: [
        { key: 'name', title: 'Exame', indexPrefix: true, flex: 2 },
        { key: 'value', title: 'Quantidade', align: 'right', flex: 1 },
      ],
      specialtyColumns: [
        { key: 'name', title: 'Top 5 Exames', indexPrefix: true, flex: 2 },
        {
          key: 'value',
          title: 'Total (vs. mês anterior)',
          align: 'right',
          deltaKey: 'delta',
          deltaVariantKey: 'deltaVariant',
          flex: 1,
        },
      ],
    }
  },
  computed: {
    hasProfileData() {
      return hasChartData(this.topExamsByProfile, 'sortValue')
    },
    hasSpecialtyData() {
      return (
        hasChartData(this.specialtyEvolution) ||
        hasChartData(this.topSpecialtyExams, 'sortValue')
      )
    },
    selectedSpecialtyDescription() {
      return this.specialtyFilters[this.selectedSpecialtyIndex]?.description || ''
    },
    currentPeriod() {
      return new Date().toISOString().slice(0, 7)
    },
  },
  watch: {
    period(val) {
      if (val) this.fetchAll(val)
    },
    selectedGender(val) {
      this.fetchTopExamesPerfil(
        this.period || this.currentPeriod,
        val,
        this.selectedAgeRange
      )
    },
    selectedAgeRange(val) {
      this.fetchTopExamesPerfil(
        this.period || this.currentPeriod,
        this.selectedGender,
        val
      )
    },
    selectedSpecialtyIndex(index) {
      const specialty = this.specialtyFilters[index]
      if (specialty) {
        this.fetchDemandaSazonal(this.period || this.currentPeriod, specialty.code)
      }
    },
  },
  created() {
    this.fetchAll(this.period || this.currentPeriod)
  },
  methods: {
    fetchAll(period) {
      this.fetchPerfilExames(period)
    },
    async fetchPerfilExames(period) {
      this.loading.overview = true
      this.ageGenderItems = []
      this.topExamsByProfile = []
      this.months = []
      this.specialtyFilters = []
      this.specialtyEvolution = []
      this.topSpecialtyExams = []
      try {
        const { data } = await biDemographicProfile.getPerfilExames(period)
        this.mapPerfilExames(data)
      } catch (e) {
        console.error('[BI] Erro ao buscar perfil-exames:', e)
      } finally {
        this.loading.overview = false
      }
    },
    async fetchTopExamesPerfil(period, genero, faixaEtaria) {
      this.loading.profile = true
      this.topExamsByProfile = []
      try {
        const { data } = await biDemographicProfile.getTopExamesPerfil(
          period,
          genero,
          faixaEtaria
        )
        this.topExamsByProfile = this.mapTopItems(data?.items)
      } catch (e) {
        console.error('[BI] Erro ao buscar top-exames-perfil:', e)
      } finally {
        this.loading.profile = false
      }
    },
    async fetchDistribuicaoEtaria(period, exame) {
      this.loading.ageGender = true
      this.ageGenderItems = []
      try {
        const { data } = await biDemographicProfile.getDistribuicaoEtaria(period, exame)
        this.ageGenderItems = data?.items ?? []
      } catch (e) {
        console.error('[BI] Erro ao buscar distribuicao-etaria:', e)
      } finally {
        this.loading.ageGender = false
      }
    },
    async fetchDemandaSazonal(period, especialidadeCodigo) {
      this.loading.seasonal = true
      this.months = []
      this.specialtyEvolution = []
      this.topSpecialtyExams = []
      try {
        const { data } = await biDemographicProfile.getDemandaSazonal(
          period,
          especialidadeCodigo
        )
        const evolution = data?.seasonalDemand?.items ?? []
        this.months = evolution.map(item => this.formatMonth(item.period))
        this.specialtyEvolution = evolution.map(item => item.quantity)
        this.topSpecialtyExams = this.mapSpecialtyExams(data?.topExams?.items)
      } catch (e) {
        console.error('[BI] Erro ao buscar demanda-sazonal:', e)
      } finally {
        this.loading.seasonal = false
      }
    },
    mapPerfilExames(data) {
      if (!data) return
      this.kpis = this.buildKpis(data.summary)
      this.availableExams = data.availableExams ?? []

      const ageGender = data.charts?.examsByAgeAndGender
      this.selectedExam = ageGender?.examFilter || 'ALL'
      this.ageGenderItems = ageGender?.items ?? []

      const profile = data.charts?.topExamsByProfile
      this.selectedGender = profile?.gender || 'ALL'
      this.selectedAgeRange = profile?.ageRange || 'ALL'
      this.topExamsByProfile = this.mapTopItems(profile?.items)

      this.specialtyFilters = data.charts?.topSpecialties ?? []
      this.selectedSpecialtyIndex = 0
      const specialty = this.specialtyFilters[0]
      if (specialty) {
        this.fetchDemandaSazonal(data.period?.value, specialty.code)
      } else {
        this.months = []
        this.specialtyEvolution = []
        this.topSpecialtyExams = []
      }

      if (data.dataStatus?.updatedAt) {
        this.$emit('updated-at', data.dataStatus.updatedAt)
      }
    },
    buildKpis(summary) {
      if (!summary) return []
      const items = [
        { key: 'ordersRegistered', title: 'OS cadastradas' },
        { key: 'examsPerformed', title: 'Exames realizados' },
        { key: 'avgExamsPerOrder', title: 'Média de Exames por OS' },
      ]
      return items.map(({ key, title }) => {
        const kpi = summary[key] || {}
        const change = kpi.changeVsPreviousMonth
        return {
          title,
          value:
            key === 'avgExamsPerOrder'
              ? (kpi.value ?? 0).toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : (kpi.value ?? 0).toLocaleString('pt-BR'),
          complement:
            typeof change === 'number'
              ? `${change >= 0 ? '+' : ''}${Math.round(change * 100)}%`
              : '',
          complementVariant: change >= 0 ? 'green' : 'red',
          description: 'vs. mês anterior',
        }
      })
    },
    mapTopItems(items) {
      return (items ?? []).map(item => ({
        name: item.description,
        value: item.quantity?.toLocaleString('pt-BR') ?? '--',
        sortValue: item.quantity,
      }))
    },
    mapSpecialtyExams(items) {
      return (items ?? []).map(item => {
        const change = item.changeVsPreviousMonth
        return {
          name: item.description,
          value: item.quantity?.toLocaleString('pt-BR') ?? '--',
          delta:
            typeof change === 'number'
              ? `${change >= 0 ? '+' : ''}${Math.round(change * 100)}%`
              : '',
          deltaVariant: change >= 0 ? 'green' : 'red',
          sortValue: item.quantity,
        }
      })
    },
    formatMonth(period) {
      const [year, month] = period.split('-')
      const monthLabels = [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ]
      return `${monthLabels[Number(month) - 1]}/${year.slice(-2)}`
    },
    formatExamCount(value) {
      if (!Number.isFinite(value)) return '-'
      return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
    },
    onExamChange(exam) {
      this.selectedExam = exam
      this.fetchDistribuicaoEtaria(this.period || this.currentPeriod, exam)
    },
    getProfileExams() {
      return [...this.topExamsByProfile].sort((a, b) => b.sortValue - a.sortValue)
    },
    getSpecialtyEvolution() {
      return this.specialtyEvolution
    },
    getSpecialtyExams() {
      return [...this.topSpecialtyExams].sort((a, b) => b.sortValue - a.sortValue)
    },
    onViewAll() {
      this.$emit('view-all')
    },
  },
}
</script>

<style scoped lang="scss">
.demographic-profile {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__cards {
    display: flex;
    gap: 16px;
  }

  &__grid {
    display: flex;
    gap: 16px;

    &--top > * {
      flex: 1;
      min-width: 0;
    }

    &--specialties > * {
      flex: 1;
      min-width: 0;
    }
  }

  &__panel {
    display: flex;
    flex-direction: column;
    padding: 16px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  }

  &__panel-title {
    margin-bottom: 16px;
    color: rgba(70, 70, 70, 1);
    font-size: 16px;
    font-weight: 700;
    font-family: 'Roboto', sans-serif;
  }

  &__list-skeleton {
    display: flex;
    flex-direction: column;
    gap: 8px;

    span {
      height: 48px;
      border-bottom: 1px solid rgba(230, 230, 230, 1);
      background: linear-gradient(90deg, #f2f2f2 25%, #e9e9e9 50%, #f2f2f2 75%);
      background-size: 200% 100%;
      animation: demographic-profile-shimmer 1.4s ease-in-out infinite;
    }
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;

    &--profile {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }
  }

  &__select {
    width: 100%;
    height: 44px;
    padding: 0 44px 0 12px;
    border: 1px solid #d5d5d5;
    border-radius: 8px;
    font-family: 'Exo 2', sans-serif;
    font-size: 13.33px;
    font-weight: 500;
    color: rgba(153, 153, 153, 1);
    background: #fff
      url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.33031 11.399L11.2159 14.2752C11.6504 14.7083 12.3523 14.7083 12.7868 14.2752L15.6724 11.399C16.3743 10.6993 15.873 9.5 14.8814 9.5H9.1102C8.11863 9.5 7.62841 10.6993 8.33031 11.399Z' fill='%237F1A54'/%3E%3C/svg%3E")
      no-repeat right 8px center / 28px 28px;
    cursor: pointer;
    appearance: none;

    &:hover,
    &:focus {
      border-color: #7f1a54;
      outline: none;
    }
  }

  &__filters button {
    height: inherit;
    padding: 8px 16px;
    border: 1px solid #7f1a54;
    border-radius: 4px;
    background: #fff;
    color: #7f1a54;
    font-size: 11.11px;
    font-family: 'Exo 2', sans-serif;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(.demographic-profile__filter--active) {
      background: #fff;
      border-color: rgba(236, 32, 114, 1);
      color: rgba(236, 32, 114, 1);
    }
  }

  &__filters .demographic-profile__filter--active {
    background: #7f1a54;
    color: #fff;
  }

  &__view-all {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 4px;
    margin-top: 12px;
    padding: 8px;
    border: 0;
    background: #fdf7f9;
    color: #7f1a54;
    font-family: 'Exo 2', sans-serif;
    font-weight: 500;
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;

    .md-icon {
      width: 24px;
      height: 24px;
      min-width: 24px;
      min-height: 24px;
      margin: 0;
    }

    // garante a cor do ícone independente da ordem de carregamento do CSS entre ambientes
    ::v-deep svg .color {
      fill: #7f1a54 !important;
    }
  }

  @media (max-width: 768px) {
    &__cards,
    &__grid {
      flex-direction: column;
    }
  }
}

@keyframes demographic-profile-shimmer {
  to {
    background-position-x: -200%;
  }
}
</style>
